import { cn } from "@/lib/cn";
import { isStatic } from "@/shared";
import type { StaticEntityType } from "@config";
import { useIsPdf } from "@library/components";
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import { useWindowSize } from "usehooks-ts";
import { useAnimate } from "../hooks";
import {
  RkpiWrapper,
  TextContainerStyled,
  WrapperStyled,
} from "../styles/Numbers.styled";
import type {
  AnimatedNumbersProps,
  EditorDataProps,
  EditorSlotsProps,
} from "../types";
import {
  animateRkpi,
  countDigits,
  formatNumberWithCommas,
  getSpeed,
  parseNumericValue,
  setRkpiInnerHtml,
} from "../utils";
import { Fluid } from "./Fluid";

const EDITOR_ATTRIBUTES = ({ isEditor }: { isEditor: boolean }) => {
  const payload: Record<string, string> = {
    "data-dnd-trigger": "true",
  };
  return isEditor ? payload : {};
};

export const NumbersLibrary = ({
  entity,
  editorSlots,
  editorData,
}: {
  entity: StaticEntityType;
  editorSlots?: EditorSlotsProps;
  editorData?: EditorDataProps;
}) => {
  const { settings } = entity as StaticEntityType<AnimatedNumbersProps>;
  const width = editorData?.width || settings.width;
  const isDynamic = settings.dynamicVaribleNumber;
  const hasDynamicVariable =
    isDynamic && settings.dynamicVaribleNumber?.startsWith("{{");
  const dynamicVariableParsed =
    isDynamic && !settings.dynamicVaribleNumber.startsWith("{{");

  const [play, setPlay] = useState<boolean>(false);
  const [number, setNumber] = useState<number>(0);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const rkpiRef = useRef<HTMLDivElement>(null);
  const duration = getSpeed(settings.speed);
  const size = useWindowSize();
  const isStaticView = isStatic();
  const isPdf = useIsPdf();
  const observerKey = useAnimate({ ref: rkpiRef });

  const validateFastVersioning = () => {
    if (dynamicVariableParsed) {
      const parsed = parseNumericValue(settings.dynamicVaribleNumber);
      if (Number.isFinite(parsed)) return setNumber(parsed);
    }
    const fallback = parseNumericValue(settings.number);
    setNumber(Number.isFinite(fallback) ? fallback : 0);
  };

  useEffect(() => {
    validateFastVersioning();
    let playTimeout: null | ReturnType<typeof setTimeout> = null;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          observer.disconnect();

          playTimeout = setTimeout(() => {
            setPlay(true);
            playTimeout = null;
          }, 200);
        }
      });
    });

    if (wrapperRef.current) observer.observe(wrapperRef.current);
    return () => {
      observer.disconnect();
      if (playTimeout != null) clearTimeout(playTimeout);
    };
  }, []);

  useEffect(() => {
    if (rkpiRef && rkpiRef.current) {
      if (isPdf || isStaticView) {
        setRkpiInnerHtml(rkpiRef.current, number, {
          ...settings,
        });
        return;
      }

      setRkpiInnerHtml(rkpiRef.current, 0, {
        ...settings,
      });

      if (play) {
        const { stop } = animateRkpi(
          rkpiRef.current,
          0,
          number,
          duration * 1000,
          0,
          settings.prefix,
          settings.suffix,
          settings.decimal,
        );

        return () => {
          stop();
        };
      }
    }
  }, [
    play,
    number,
    settings.prefix,
    settings.suffix,
    duration,
    settings.decimal,
    settings.fontSize,
    size.width,
    isPdf,
    isStaticView,
    observerKey,
  ]);

  const displayAutoVersion =
    settings.fontSize === "Auto" ||
    (size.width < 576 && Number(settings.fontSize) > 64);

  const RKPIElement = useMemo(
    () => (
      <div
        className="rkpi__number tw-flex [align-items:inherit] [font-family:var(--title-font)] [justify-content:inherit] tw-break-all"
        ref={rkpiRef}
      ></div>
    ),
    [settings.fontSize],
  );

  useEffect(
    () => validateFastVersioning(),
    [settings.number, settings.dynamicVaribleNumber],
  );

  return (
    <RkpiWrapper
      className={cn("rkpi tw-flex tw-items-center tw-leading-none", {
        "tw-outline-primary tw-rounded-[2px] tw-outline tw-outline-2":
          !!editorData && editorData.active && settings.fontSize !== "Auto",
        "tw-outline tw-outline-2 tw-outline-[#FFB82E]":
          !!editorData && !!hasDynamicVariable && settings.fontSize !== "Auto",
      })}
      ref={wrapperRef}
      {...EDITOR_ATTRIBUTES({ isEditor: !!editorData && !displayAutoVersion })}
      $horizontalAlignment={settings.horizontalAlignment}
      $color={settings.color}
      $fontSize={settings.fontSize}
      $isBold={settings.isBold}
    >
      {editorSlots?.dynamicVariableDotSlot}
      {displayAutoVersion ? (
        <WrapperStyled $hasDynamicVar={!!hasDynamicVariable}>
          {editorSlots?.resizeSlot}
          <TextContainerStyled
            ref={editorData?.elementRef}
            $isBold={settings.isBold}
            $isSmallWidth={!!(width && width < 50)}
            style={
              {
                "--entity-size": `${size.width < 576 && Number(settings.fontSize) > 56 ? 80 : width}%`,
              } as CSSProperties
            }
            {...EDITOR_ATTRIBUTES({ isEditor: !!editorData })}
          >
            {editorSlots?.dynamicVariablePlateSlot}
            <Fluid maxFontSizePx={1920}>
              <div className="rkpi__hidden tw-flex [align-items:inherit] [font-family:var(--title-font)] [justify-content:inherit]">
                {(settings.prefix || "") +
                  formatNumberWithCommas(
                    Number(Number(number).toFixed(countDigits(number))),
                    settings.decimal,
                  ) +
                  (settings.suffix || "")}
              </div>
              {RKPIElement}
            </Fluid>
          </TextContainerStyled>
        </WrapperStyled>
      ) : (
        <>
          {editorSlots?.dynamicVariablePlateSlot}
          {editorSlots?.dynamicVariableDotSlot}
          {RKPIElement}
        </>
      )}
    </RkpiWrapper>
  );
};
